import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getHomePageData } from "../../api/home.js";
import {
  GraduationCap,
  ArrowRight,
  BookOpen,
  Briefcase,
  Users,
  Award,
  ShoppingBag,
  Zap,
  CheckCircle2,
} from "lucide-react";

function Hero() {
  const navigate = useNavigate();
  const [currentStat, setCurrentStat] = useState(0);
  const [statsdata, setStatsData] = useState({
    activeStudents: 52000,
    universities: 48,
    resources: 1250,
    jobListings: 340,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getHomePageData();
        if (response && response.data) {
          setStatsData({
            activeStudents: response.data.totalUsers || 52000,
            universities: response.data.totalResults || 48,
            resources: response.data.totalResources || 1250,
            jobListings: response.data.totalJobs || 340,
          });
        }
      } catch (error) {
        console.warn("Home page API fallback to default metrics:", error);
      }
    };

    fetchData();
  }, []);

  const stats = [
    { number: statsdata.activeStudents.toLocaleString() + "+", label: "Active Students" },
    { number: statsdata.universities + "+", label: "Universities" },
    { number: statsdata.resources + "+", label: "Study Resources" },
    { number: statsdata.jobListings + "+", label: "Job Listings" },
  ];

  const features = [
    {
      icon: BookOpen,
      title: "Study Resources",
      description: "Access handwritten notes, exam papers, and video tutorials",
      color: "from-blue-500 to-indigo-600",
      link: "/notes",
    },
    {
      icon: Briefcase,
      title: "Jobs & Internships",
      description: "Discover off-campus drives and remote internships",
      color: "from-purple-500 to-pink-600",
      link: "/jobs",
    },
    {
      icon: Users,
      title: "Campus Community",
      description: "Connect with peers, share updates, and join discussions",
      color: "from-emerald-500 to-teal-600",
      link: "/community",
    },
    {
      icon: Award,
      title: "University Results",
      description: "Instant access to official university marksheets",
      color: "from-amber-500 to-rose-600",
      link: "/result",
    },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentStat((prev) => (prev + 1) % stats.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [stats.length]);

  return (
    <div className="bg-gradient-to-b from-slate-900 via-indigo-950 to-slate-900 text-white min-h-screen relative overflow-hidden">
      {/* Background Decorative Gradients */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-600/20 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-10 right-10 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* Left Column Content */}
          <div className="lg:col-span-7 space-y-6 text-center lg:text-left">
            <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/15 text-xs sm:text-sm font-semibold text-purple-300">
              <GraduationCap size={18} className="text-purple-400" />
              <span>India's #1 Campus & Student Ecosystem</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight">
              One Stop Campus Solution for{" "}
              <span className="bg-gradient-to-r from-blue-400 via-purple-300 to-pink-400 bg-clip-text text-transparent">
                Students
              </span>
            </h1>

            <p className="text-base sm:text-lg text-slate-300 max-w-2xl mx-auto lg:mx-0 font-normal leading-relaxed">
              Connect with peers, prepare for placements, access study notes, check university results, and buy/sell gadgets — all in one powerful platform.
            </p>

            {/* CTA Button Group */}
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3.5 pt-2">
              <Link to="/community">
                <button className="px-6 py-3.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white rounded-2xl font-bold text-sm shadow-lg shadow-purple-500/25 hover:shadow-xl transition-all transform hover:-translate-y-0.5 flex items-center space-x-2">
                  <Users size={18} />
                  <span>Join Community</span>
                </button>
              </Link>

              <Link to="/marketplace">
                <button className="px-6 py-3.5 bg-white/10 hover:bg-white/20 text-white border border-white/20 rounded-2xl font-bold text-sm transition-all flex items-center space-x-2">
                  <ShoppingBag size={18} className="text-pink-400" />
                  <span>MarketPlace</span>
                </button>
              </Link>

              <Link to="/jobs">
                <button className="px-6 py-3.5 bg-white/10 hover:bg-white/20 text-white border border-white/20 rounded-2xl font-bold text-sm transition-all flex items-center space-x-2">
                  <Briefcase size={18} className="text-blue-400" />
                  <span>Browse Jobs</span>
                </button>
              </Link>
            </div>

            {/* Live Stats */}
            <div className="pt-6 border-t border-white/10 grid grid-cols-2 sm:grid-cols-4 gap-4">
              {stats.map((stat, idx) => (
                <div
                  key={idx}
                  className={`p-3 rounded-2xl transition-all ${
                    currentStat === idx
                      ? "bg-white/10 border border-purple-400/30 scale-105"
                      : "bg-white/5 border border-white/5"
                  }`}
                >
                  <div className="text-xl sm:text-2xl font-extrabold text-white">
                    {stat.number}
                  </div>
                  <div className="text-xs text-slate-400 font-medium">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Column Interactive Hub Card */}
          <div className="lg:col-span-5 relative">
            <div className="bg-gradient-to-tr from-purple-600/30 to-blue-600/30 p-1 rounded-3xl backdrop-blur-xl border border-white/20 shadow-2xl">
              <div className="bg-slate-900/90 rounded-[22px] p-6 space-y-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  </div>
                  <span className="text-xs text-slate-400 font-mono">
                    CollegeConnect Hub v2.0
                  </span>
                </div>

                <div className="space-y-3">
                  <div className="p-4 rounded-xl bg-purple-900/40 border border-purple-500/30 flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-lg bg-purple-600/80 flex items-center justify-center text-white">
                        <Zap size={20} />
                      </div>
                      <div>
                        <h4 className="font-bold text-sm text-white">
                          Placement Drive 2026
                        </h4>
                        <p className="text-xs text-slate-400">
                          Top tech companies recruiting
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => navigate("/community")}
                      className="px-3 py-1.5 bg-purple-500 text-white rounded-lg text-xs font-bold hover:bg-purple-600 transition-colors"
                    >
                      View
                    </button>
                  </div>

                  <div className="p-4 rounded-xl bg-blue-900/40 border border-blue-500/30 flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-600/80 flex items-center justify-center text-white">
                        <BookOpen size={20} />
                      </div>
                      <div>
                        <h4 className="font-bold text-sm text-white">
                          Semester Notes Hub
                        </h4>
                        <p className="text-xs text-slate-400">
                          1,200+ curated study materials
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => navigate("/notes")}
                      className="px-3 py-1.5 bg-blue-500 text-white rounded-lg text-xs font-bold hover:bg-blue-600 transition-colors"
                    >
                      Explore
                    </button>
                  </div>

                  <div className="p-4 rounded-xl bg-emerald-900/40 border border-emerald-500/30 flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-lg bg-emerald-600/80 flex items-center justify-center text-white">
                        <CheckCircle2 size={20} />
                      </div>
                      <div>
                        <h4 className="font-bold text-sm text-white">
                          University Results
                        </h4>
                        <p className="text-xs text-slate-400">
                          Verified marksheets portal
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => navigate("/result")}
                      className="px-3 py-1.5 bg-emerald-500 text-white rounded-lg text-xs font-bold hover:bg-emerald-600 transition-colors"
                    >
                      Check
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Feature Cards Grid */}
        <div className="mt-20">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-extrabold text-white mb-2">
              Everything You Need in One Place
            </h2>
            <p className="text-slate-400 text-sm max-w-xl mx-auto">
              Power up your campus life with dedicated academic and placement utilities.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <div
                  key={idx}
                  onClick={() => navigate(feature.link)}
                  className="group bg-slate-800/60 hover:bg-slate-800 border border-slate-700/80 hover:border-purple-500/50 rounded-2xl p-6 transition-all duration-300 transform hover:-translate-y-1.5 cursor-pointer shadow-lg"
                >
                  <div
                    className={`w-14 h-14 rounded-2xl bg-gradient-to-r ${feature.color} flex items-center justify-center text-white mb-4 shadow-md group-hover:scale-110 transition-transform`}
                  >
                    <Icon size={24} />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-1.5 group-hover:text-purple-300 transition-colors">
                    {feature.title}
                  </h3>
                  <p className="text-xs text-slate-400 leading-relaxed mb-4">
                    {feature.description}
                  </p>

                  <div className="flex items-center text-xs font-bold text-purple-400 group-hover:text-purple-300">
                    <span>Access Feature</span>
                    <ArrowRight
                      size={14}
                      className="ml-1 group-hover:translate-x-1 transition-transform"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Hero;
